import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Box,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';

import FacilitiesAutocomplete from '@/components/FacilitiesAutocomplete/FacilitiesAutocomplete';
import { useGetFacilityGroupsForForms } from '@/api/hooks/facility-groups/useGetFacilityGroupsForForms';
import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';
import { useGetFacilityGroupTree } from '@/api/hooks/facility-groups/useGetFacilityGroupTree';

import FacilityGroupTreeSelector from './FacilityGroupTreeSelector';

const FacilityAccessComponent = ({
  accessMode,
  onAccessModeChange,
  permissionsType,
  roleBasedFacilitiesAccess,
  selectedFacilities,
  onSelectedFacilitiesChange,
  selectedFacilityGroupIds,
  onSelectedFacilityGroupIdsChange,
  selectedOrganization,
  errors,
}) => {
  const shouldCheckFacilityGroupAvailability =
    !!selectedOrganization &&
    permissionsType === 'role' &&
    !roleBasedFacilitiesAccess;
  const shouldFetchFacilityGroupTree =
    shouldCheckFacilityGroupAvailability &&
    accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS;

  const {
    facilityGroups,
    loading: facilityGroupAvailabilityLoading,
  } = useGetFacilityGroupsForForms({
    organizationId: selectedOrganization,
    enabled: shouldCheckFacilityGroupAvailability,
  });

  const {
    treeData: facilityGroupTree,
    loading: facilityGroupsLoading,
  } = useGetFacilityGroupTree(selectedOrganization, {
    enabled: shouldFetchFacilityGroupTree,
  });

  const hasFacilityGroups = facilityGroups.length > 0;
  const facilityGroupModeDisabledReason = roleBasedFacilitiesAccess
    ? 'The selected role already grants all facilities access.'
    : permissionsType !== 'role'
    ? 'Facility groups are available only for role-based users.'
    : !selectedOrganization
    ? 'Select an organization first.'
    : !hasFacilityGroups && !facilityGroupAvailabilityLoading
    ? 'This organization does not have any facility groups yet.'
    : '';
  const isFacilityGroupModeDisabled = Boolean(facilityGroupModeDisabledReason);

  const renderAccessModeOption = (
    value,
    label,
    disabled = false,
    reason = '',
  ) => {
    const labelContent =
      disabled && reason ? (
        <Box component='span' sx={{ display: 'block' }}>
          <Typography component='span' color='text.disabled'>
            {label}
          </Typography>
          <FormHelperText
            component='span'
            disabled
            sx={{ display: 'block', m: 0, mt: 0.25 }}>
            {reason}
          </FormHelperText>
        </Box>
      ) : (
        <Typography component='span' color='text.primary'>
          {label}
        </Typography>
      );

    const option = (
      <FormControlLabel
        value={value}
        control={
          <Radio
            sx={
              disabled
                ? {
                    color: 'text.disabled',
                    '&.Mui-checked': { color: 'text.disabled' },
                    '&.Mui-disabled': { color: 'text.disabled' },
                  }
                : undefined
            }
          />
        }
        label={labelContent}
        disabled={disabled}
        sx={{
          alignItems: disabled && reason ? 'flex-start' : 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          '& .MuiRadio-root': {
            mt: disabled && reason ? -0.5 : 0,
          },
        }}
      />
    );

    if (!disabled || !reason) {
      return option;
    }

    return (
      <Tooltip title={reason} placement='top'>
        <span>{option}</span>
      </Tooltip>
    );
  };

  const facilityAccessInfoMessages = [
    ...(roleBasedFacilitiesAccess
      ? [
          'This role grants all facilities access. Switch to a different role to use direct facilities or facility groups.',
        ]
      : []),
    ...(accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES
      ? [
          'User has access to all facilities in this organization.',
          'Access will apply to future facilities as well.',
        ]
      : []),
    ...(accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS
      ? ['Selecting a parent group automatically includes descendant groups.']
      : []),
    ...(permissionsType !== 'role'
      ? [
          'Facility groups remain unavailable until you switch this user to role-based permissions.',
        ]
      : []),
  ];

  return (
    <>
      <FormControl component='fieldset' sx={{ mb: 3 }}>
        <RadioGroup
          value={accessMode}
          onChange={(event) => onAccessModeChange(event.target.value)}>
          {renderAccessModeOption(
            FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES,
            'Direct facilities',
            roleBasedFacilitiesAccess,
            'Choose a role that does not grant all facilities access to assign specific facilities.',
          )}
          {renderAccessModeOption(
            FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS,
            'Facility groups',
            isFacilityGroupModeDisabled,
            facilityGroupModeDisabledReason,
          )}
          {renderAccessModeOption(
            FACILITY_USER_ACCESS_MODE.ALL_FACILITIES,
            'All facilities',
          )}
        </RadioGroup>
      </FormControl>

      {facilityAccessInfoMessages.length > 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {facilityAccessInfoMessages.map((message) => (
              <Typography key={message} variant='body2'>
                {message}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}

      {accessMode === FACILITY_USER_ACCESS_MODE.NONE && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Select a new facility access mode before saving.
        </Alert>
      )}

      {errors.accessMode && (
        <Typography variant='body2' color='error' sx={{ mb: 2 }}>
          {errors.accessMode}
        </Typography>
      )}

      {accessMode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS && (
        <Box sx={{ mb: 2 }}>
          {facilityGroupsLoading ? (
            <Typography variant='body2' color='text.secondary'>
              Loading facility groups...
            </Typography>
          ) : facilityGroupTree.length > 0 ? (
            <FacilityGroupTreeSelector
              treeData={facilityGroupTree}
              value={selectedFacilityGroupIds}
              onChange={onSelectedFacilityGroupIdsChange}
              organizationId={selectedOrganization}
              helperText={errors.facility_groups}
            />
          ) : (
            <Alert severity='warning'>
              This organization does not have any facility groups yet.
            </Alert>
          )}
        </Box>
      )}

      {accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES && (
        <>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Select specific facilities for this user.
          </Typography>

          <Box sx={{ mb: 2 }}>
            {selectedOrganization && (
              <FacilitiesAutocomplete
                value={selectedFacilities}
                onChange={onSelectedFacilitiesChange}
                label='Facilities'
                placeholder='Search and select facilities...'
                size='small'
                organizationId={selectedOrganization}
                error={!!errors.facilities}
                helperText={errors.facilities}
              />
            )}
          </Box>

          {selectedFacilities.length === 0 && (
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              No facilities selected. Please select at least one facility for
              this user.
            </Typography>
          )}
        </>
      )}
    </>
  );
};

export default FacilityAccessComponent;
